/* The document pane — the cited page, with the quoted span marked on it.
 *
 * Before this existed every finding said "Redo this yourself from the source
 * document" and named a page the reviewer had no way to see. That is the
 * product's largest trust gap: without the surrounding page a reviewer cannot
 * judge whether a flag is fair, and a flag they cannot judge is one they end up
 * disputing. BRIEF.md defect 5.
 *
 * The page comes from text the engine already extracted — `pages:<docId>` in
 * IndexedDB for a plan the reviewer opened, a shipped chunk for the worked
 * example. No PDF is parsed here and no rendering canvas is involved; this is a
 * text pane, and it says so by looking like one.
 *
 * The one rule everything else serves: the mark is drawn only where a stored
 * span slices the finding's own words back out of this page's text. Where it
 * cannot, the pane says so in plain words and shows the quote it read. It never
 * highlights a near-enough match, and it never quietly shows an unmarked page
 * as though the quote had been found. */

import { useEffect, useId, useRef, useState, type FormEvent } from 'react';

import { Button, EmptyState, Quote } from '../primitives';
import { evidenceBadgeFor } from '../../data/report';
import {
  announcementFor,
  documentView,
  pageLabel,
  parsePageInput,
  unlocatedNotice,
} from './documentPaneModel';
import type { ExtractedPages, Flag } from '../../types';
import './DocumentPane.css';

export interface DocumentPaneProps {
  /** The document this review is of, named so the pane is not anonymous. */
  fileName: string;
  /** The finding whose page the pane opens on. */
  finding: Flag | null;
  /** The extracted page text, or null while it is being read or if absent. */
  pages: ExtractedPages | null;
  /** True while the page text is being read out of storage. */
  loading?: boolean;
  /** The page the reviewer navigated to; null to follow the finding. */
  page: number | null;
  onPageChange(page: number): void;
  /**
   * Returns the reviewer to the finding the pane is showing. Present so that
   * someone who followed a citation into the page has a way back that is not
   * the browser's back button, which in a single-route workspace would leave
   * the review entirely.
   */
  onBackToFinding?(): void;
  /**
   * Non-zero when the reviewer asked to be taken to the span, rather than
   * merely selecting a finding. Focus moves into the highlight and the pane
   * calls onFocusHandled; selecting a finding scrolls and announces but leaves
   * focus where the reviewer put it. See docs/redesign/REPORT.md.
   *
   * A request rather than a counter, so that collapsing and reopening the pane
   * — which unmounts and remounts it — cannot replay a focus move the reviewer
   * asked for ten minutes ago.
   */
  focusRequest?: number;
  onFocusHandled?(): void;
}

export default function DocumentPane({
  fileName,
  finding,
  pages,
  loading = false,
  page,
  onPageChange,
  onBackToFinding,
  focusRequest = 0,
  onFocusHandled,
}: DocumentPaneProps) {
  const view = documentView({ finding, pages, page, loading });
  const bodyRef = useRef<HTMLDivElement>(null);
  const markRef = useRef<HTMLElement>(null);

  const currentPage = view.kind === 'page' ? view.page : null;
  const highlighted = view.kind === 'page' && view.segments !== null;

  /* --- the span is brought into view whenever it changes --------------------
     Only this pane's own scroll box is moved, computed from the two boxes
     rather than delegated to scrollIntoView, which would also scroll the
     workspace column and the page behind it. */

  useEffect(() => {
    if (!highlighted) return;
    const body = bodyRef.current;
    const mark = markRef.current;
    if (!body || !mark) return;

    const frame = requestAnimationFrame(() => {
      const bodyBox = body.getBoundingClientRect();
      const markBox = mark.getBoundingClientRect();
      if (markBox.top >= bodyBox.top && markBox.bottom <= bodyBox.bottom) return;
      // A third of the way down, so the lines above the quote — the ones that
      // give it its context — are on screen with it.
      body.scrollTop += markBox.top - bodyBox.top - bodyBox.height / 3;
    });
    return () => cancelAnimationFrame(frame);
  }, [highlighted, finding?.id, currentPage]);

  /* --- and takes focus when the reviewer asked to be taken to it ----------
     The request is answered once and then cleared, so a pane that is collapsed
     and reopened later does not take focus a second time for a citation the
     reviewer followed long ago. */

  useEffect(() => {
    if (focusRequest === 0) return;
    if (highlighted) markRef.current?.focus();
    else bodyRef.current?.focus();
    onFocusHandled?.();
  }, [focusRequest, highlighted, finding?.id, currentPage, onFocusHandled]);

  return (
    <div className="dp">
      <div className="dp__head">
        <span className="dp__name" title={fileName}>
          {fileName}
        </span>
        {view.kind === 'page' ? (
          <PageControls
            page={view.page}
            pageCount={view.pageCount}
            onPageChange={onPageChange}
          />
        ) : null}
      </div>

      <div className="dp__body" ref={bodyRef} tabIndex={-1}>
        {view.kind === 'no-finding' ? (
          <p className="dp__line">
            Select a finding and the page it quotes opens here.
          </p>
        ) : null}

        {view.kind === 'loading' ? (
          <p className="dp__line">Opening the document…</p>
        ) : null}

        {view.kind === 'no-page' ? (
          <EmptyState
            className="dp__empty"
            heading="This finding isn’t tied to a single page."
            body="A finding about something the plan doesn’t say has no clause to point at."
            action={
              <Button variant="secondary" marker onClick={() => onPageChange(1)}>
                Open page 1
              </Button>
            }
          />
        ) : null}

        {view.kind === 'unavailable' ? (
          <p className="dp__line">
            The page text for this document isn’t in this browser, so the pane
            can’t show it. The quote and the check below the claim are unchanged.
          </p>
        ) : null}

        {view.kind === 'page' ? (
          <>
            {view.unlocated ? (
              <div className="dp__unlocated">
                <p className="dp__notice">{unlocatedNotice(view.page)}</p>
                {finding?.quote ? (
                  <Quote tone={finding ? evidenceBadgeFor(finding).tone : 'blue'}>
                    {finding.quote}
                  </Quote>
                ) : null}
              </div>
            ) : null}

            {view.text.trim().length > 0 ? (
              <pre className="dp__text">
                {view.segments ? (
                  <>
                    {view.segments.before}
                    {/* Keyed on what it marks, so moving to another finding or
                        another page remounts it and the fade runs again. */}
                    <mark
                      key={`${finding?.id ?? ''}:${view.page}:${view.segments.before.length}`}
                      className="dp__mark"
                      ref={markRef}
                      tabIndex={-1}
                      aria-label="The passage this finding quotes"
                    >
                      {view.segments.quote}
                    </mark>
                    {view.segments.after}
                  </>
                ) : (
                  view.text
                )}
              </pre>
            ) : null}
          </>
        ) : null}
      </div>

      {finding && onBackToFinding ? (
        <div className="dp__foot">
          <Button variant="ghost" onClick={onBackToFinding}>
            Back to the finding
          </Button>
        </div>
      ) : null}

      {/* What the eye gets from the header and the mark, in words. */}
      <p className="dp__live" role="status" aria-live="polite">
        {announcementFor(view, finding)}
      </p>
    </div>
  );
}

/* ==========================================================================
   The header's page controls
   ========================================================================== */

interface PageControlsProps {
  page: number;
  pageCount: number;
  onPageChange(page: number): void;
}

/**
 * "Page 6 of 35", previous, next, and a box to jump to a page.
 *
 * The box holds the page number and is reset to it whenever the pane moves, so
 * it always shows where the reviewer is rather than what they last typed. A
 * value that is not a page in this document is refused and the box returns to
 * the current page: jumping somewhere near the number asked for would be worse
 * than not moving.
 */
function PageControls({ page, pageCount, onPageChange }: PageControlsProps) {
  const [draft, setDraft] = useState(String(page));
  const [invalid, setInvalid] = useState(false);
  const jumpId = useId();
  const rangeId = `${jumpId}-range`;

  useEffect(() => {
    setDraft(String(page));
    setInvalid(false);
  }, [page]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const next = parsePageInput(draft, pageCount);
    if (next === null) {
      setInvalid(true);
      setDraft(String(page));
      return;
    }
    setInvalid(false);
    onPageChange(next);
  };

  return (
    <div className="dp__pager">
      <span className="dp__page">{pageLabel(page, pageCount)}</span>

      <div className="dp__steps">
        <Button
          variant="ghost"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          ‹
        </Button>
        <Button
          variant="ghost"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pageCount}
          aria-label="Next page"
        >
          ›
        </Button>
      </div>

      <form className="dp__jump" onSubmit={submit}>
        <label className="dp__jump-label" htmlFor={jumpId}>
          Go to
        </label>
        <input
          id={jumpId}
          className="dp__jump-input"
          type="text"
          inputMode="numeric"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          aria-invalid={invalid || undefined}
          aria-describedby={rangeId}
          size={3}
        />
        <span className="dp__jump-range" id={rangeId}>
          of {pageCount}
        </span>
        <Button variant="ghost" type="submit">
          Go
        </Button>
      </form>
    </div>
  );
}
